/**
 * ActiveSpeakerTracker
 *
 * Analyses audio levels from remote participant MediaStreams and emits
 * an event whenever the "active speaker" changes.
 *
 * Features:
 *  - Hysteresis: a new speaker must sustain higher volume than the current
 *    speaker for SWITCH_THRESHOLD_MS before the switch happens (avoids flicker).
 *  - Camera priority: when nobody is speaking, prefer a participant with video.
 *  - Self is excluded from becoming the active speaker (your own PiP is separate).
 *  - Cleans up AudioContexts on removeStream / destroy.
 */

type SpeakerEntry = {
    uid: string;
    analyser: AnalyserNode;
    source: MediaStreamAudioSourceNode;
    dataArray: Uint8Array;
    currentLevel: number;
};

type SpeakerChangedCallback = (uid: string, prevUid: string) => void;

const ANALYSIS_FPS = 10; // ~100 ms between checks
const SWITCH_THRESHOLD_MS = 350; // new speaker must lead for this long
const SILENCE_THRESHOLD = 12; // below this = silence (0-255 scale)
const FALLBACK_DELAY_MS = 2000; // hold last speaker for this duration after silence

export class ActiveSpeakerTracker {
    private entries: Map<string, SpeakerEntry> = new Map();
    /** Single shared AudioContext for all streams — avoids browser per-context limits. */
    private audioCtx: AudioContext | null = null;
    private selfUid = "";
    private activeSpeakerUid = "";
    private candidateUid = "";
    private candidateSince = 0;
    private lastSpeechTime = 0;
    private timer: ReturnType<typeof setInterval> | null = null;
    private listeners: SpeakerChangedCallback[] = [];
    private videoFlags: Map<string, boolean> = new Map();
    private destroyed = false;

    constructor(selfUid: string) {
        this.selfUid = selfUid;
        this.timer = setInterval(() => this.analyse(), Math.floor(1000 / ANALYSIS_FPS));
    }

    /* ---- public API ---- */

    on(cb: SpeakerChangedCallback) {
        this.listeners.push(cb);
    }

    off(cb: SpeakerChangedCallback) {
        this.listeners = this.listeners.filter(l => l !== cb);
    }

    /** Register / replace a participant's stream. */
    addStream(uid: string, stream: MediaStream) {
        if (this.destroyed) return;
        if (uid === this.selfUid) return; // self is never tracked

        // Clean up previous entry for this uid if exists
        this.removeStream(uid);

        if (!window.AudioContext) return;

        try {
            const audioTracks = stream.getAudioTracks();
            if (!audioTracks.length) return;

            // Lazily create / reuse a single shared AudioContext.
            if (!this.audioCtx) {
                this.audioCtx = new AudioContext();
            }
            // Resume in case the context started in suspended state.
            if (this.audioCtx.state === "suspended") {
                this.audioCtx.resume().catch(() => { /**/ });
            }

            const source = this.audioCtx.createMediaStreamSource(stream);
            const analyser = this.audioCtx.createAnalyser();
            analyser.fftSize = 32;
            source.connect(analyser);

            const entry: SpeakerEntry = {
                uid,
                analyser,
                source,
                dataArray: new Uint8Array(analyser.frequencyBinCount),
                currentLevel: 0,
            };
            this.entries.set(uid, entry);
        } catch (e) {
            console.warn("[ActiveSpeakerTracker] Failed to add stream for", uid, e);
        }
    }

    /** Remove a participant from tracking. */
    removeStream(uid: string) {
        const entry = this.entries.get(uid);
        if (!entry) return;
        try { entry.source.disconnect(); } catch (_) { /**/ }
        // Do NOT close the shared audioCtx here — it is reused by other streams.
        this.entries.delete(uid);

        // If the removed uid was the active speaker, trigger re-evaluation immediately
        if (this.activeSpeakerUid === uid) {
            this.activeSpeakerUid = "";
            this.pickFallback();
        }
    }

    /** Update video flag for camera-priority fallback. */
    setVideoFlag(uid: string, hasVideo: boolean) {
        this.videoFlags.set(uid, hasVideo);
    }

    /** Get the current active speaker uid (may be "" if nobody). */
    getActiveSpeaker(): string {
        return this.activeSpeakerUid;
    }

    /** Tear everything down. */
    destroy() {
        this.destroyed = true;
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
        for (const entry of this.entries.values()) {
            try { entry.source.disconnect(); } catch (_) { /**/ }
        }
        this.entries.clear();
        // Close the shared context once all streams are removed.
        if (this.audioCtx) {
            try { this.audioCtx.close(); } catch (_) { /**/ }
            this.audioCtx = null;
        }
        this.listeners = [];
    }

    /* ---- internal ---- */

    private analyse() {
        if (this.destroyed) return;

        let loudestUid = "";
        let loudestLevel = 0;
        const now = Date.now();

        for (const entry of this.entries.values()) {
            entry.analyser.getByteFrequencyData(entry.dataArray as unknown as Uint8Array);
            let max = 0;
            for (let i = 0; i < entry.dataArray.length; i++) {
                if (entry.dataArray[i] > max) max = entry.dataArray[i];
            }
            entry.currentLevel = max;

            if (max > SILENCE_THRESHOLD && max > loudestLevel) {
                loudestLevel = max;
                loudestUid = entry.uid;
            }
        }

        if (loudestUid) {
            this.lastSpeechTime = now;
        }

        // Nobody speaking?
        if (!loudestUid) {
            // Keep current speaker for FALLBACK_DELAY_MS
            if (this.activeSpeakerUid && now - this.lastSpeechTime < FALLBACK_DELAY_MS) {
                return; // hold
            }
            // After delay, pick a fallback (camera priority)
            if (this.activeSpeakerUid) {
                this.pickFallback();
            }
            this.candidateUid = "";
            this.candidateSince = 0;
            return;
        }

        // Same as current speaker → reset candidate
        if (loudestUid === this.activeSpeakerUid) {
            this.candidateUid = "";
            this.candidateSince = 0;
            return;
        }

        // New candidate
        if (loudestUid !== this.candidateUid) {
            this.candidateUid = loudestUid;
            this.candidateSince = now;
            return;
        }

        // Same candidate sustained long enough → switch
        if (now - this.candidateSince >= SWITCH_THRESHOLD_MS) {
            this.switchSpeaker(loudestUid);
            this.candidateUid = "";
            this.candidateSince = 0;
        }
    }

    private switchSpeaker(uid: string) {
        if (uid === this.activeSpeakerUid) return;
        const prev = this.activeSpeakerUid;
        this.activeSpeakerUid = uid;
        for (const cb of this.listeners) {
            try { cb(uid, prev); } catch (e) { console.error(e); }
        }
    }

    /** Pick a fallback speaker when nobody is talking.
     *  Priority: participant with video > first participant. */
    private pickFallback() {
        let fallback = "";
        // Prefer someone with video
        for (const uid of this.entries.keys()) {
            if (this.videoFlags.get(uid)) {
                fallback = uid;
                break;
            }
        }
        // Otherwise first remote participant
        if (!fallback) {
            const first = this.entries.keys().next();
            if (!first.done) fallback = first.value;
        }
        this.switchSpeaker(fallback);
    }
}
